import React, { useEffect, useRef, useState } from "react";
import { FaBarcode, FaCheckCircle, FaIdCard, FaUser } from "react-icons/fa";
import { IoCheckmark } from "react-icons/io5";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Row,
} from "reactstrap";
import {
  applyClassToCharacters,
  getStringFromJSX,
  LoadingSpinner,
} from "../../constants/const";
import { GetCustomStyles, GetSelectedLanguage } from "../helper/Method";
import { addMember } from "../member/ApiMember";
import { RxCross2 } from "react-icons/rx";
import { GetLanguageString } from "../helper/Components";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  withRouter,
} from "react-router-dom/cjs/react-router-dom.min";
import { ApiEvent } from "./ApiEvent";

const EventBarCodeAttendance = ({
  history,
  eventIdFromToken,
  applyMarginTop = true,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scannedCode, setScannedCode] = useState("");
  const [scannedCodeForError, setScannedCodeForError] = useState("");
  const [loading, setLoading] = useState(false);
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [unMarkingAttendance, setUnMarkingAttendance] = useState(false);
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);
  const [markingAttendanceSuccess, setMarkingAttendanceSuccess] = useState("");
  const [markingAttendanceError, setMarkingAttendanceError] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [scanStatus, setScanStatus] = useState("ready"); // 'ready', 'scanning', 'success', 'error'
  const [error, setError] = useState(false);
  const inputBufferRef = useRef(""); // Buffer for barcode scanning
  const eventId = eventIdFromToken ? eventIdFromToken : location.state?.eventId;

  const fetchMemberDetails = (code) => {
    if (!code) return;
    setLoading(true);
    setError(false);
    setMarkingAttendanceError("");
    setMarkingAttendanceSuccess("");
    setIsAttendanceMarked(false);
    setScannedCodeForError(code);

    addMember()
      .GetMemberDetailsBySerialNumberForEventAttendance(code, eventId)
      .then((res) => {
        setMemberData({
          ...res.data,
          memberPhoto: "data:image/jpeg;base64," + res.data.memberPhoto,
        });

        const message = getStringFromJSX(
          <GetLanguageString
            props="member_member_rafiq_ex"
            value={res.data.fullName}
          />
        );

        // Apply character classes if needed
        const formattedMessage = res.data.isExRafiq
          ? getStringFromJSX(applyClassToCharacters(message, "ur"))
          : null;

        // Set the error
        if (formattedMessage) {
          setMarkingAttendanceError(formattedMessage);
        }

        if (res.data.attendanceExists) {
          setScanStatus("ready");
          setMarkingAttendanceError("Error: " + t("already_marked_attendance"));
        } else {
          setScanStatus("success");
        }

        setScannedCode(""); // Clear the input field on error
        setLoading(false);
      })
      .catch((err) => {
        setError(true);

        let serverMessage = "event_attendance_barcode_error_marking_attendance";
        let errorType = "event_attendance_barcode_error_marking_attendance";

        if (err.response) {
          if (err.response.data?.message) {
            serverMessage = err.response.data.message;
          } else if (typeof err.response.data === "string") {
            serverMessage = err.response.data;
          }

          if (err.response.data?.type) {
            errorType = err.response.data.type;
          } else if (typeof err.response.data === "string") {
            errorType = err.response.data;
          }

          // Handle 500 internal error gracefully
          if (err.response.status === 500) {
            serverMessage =
              "Serial number might be incorrect or record doesn't exist.";
          }
        }
        if (
          serverMessage.includes("already_marked_attendance") ||
          errorType.includes("already_marked_attendance")
        ) {
          setScanStatus("");
        } else {
          setScanStatus("error");
        }
        setMarkingAttendanceError("Error: " + t(serverMessage));

        setMemberData("");
        setMarkingAttendance(false);
        setLoading(false);
        setScannedCode(""); // Clear the input field on error
      });
  };

  // Update your useEffect for barcode scanning
  useEffect(() => {
    let timer = null;

    const handleKeyPress = (e) => {
      // Ignore if input field is focused
      if (document.activeElement.tagName === "INPUT") return;

      if (e.key === "Enter") {
        if (inputBufferRef.current) {
          fetchMemberDetails(inputBufferRef.current);
          inputBufferRef.current = "";
        }
        return;
      }

      inputBufferRef.current += e.key;

      clearTimeout(timer);
      timer = setTimeout(() => {
        inputBufferRef.current = "";
        setScanStatus("ready");
      }, 100); // Reduced timeout for better performance
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => window.removeEventListener("keypress", handleKeyPress);
    // eslint-disable-next-line
  }, []);

  // Update your manual input handling
  const handleManualInput = (e) => {
    if (scanStatus !== "success") setScannedCode(e.target.value);
  };

  const handleManualSubmit = (e) => {
    if (e.key === "Enter" && e.target.value.trim() !== "") {
      // Clear the buffer to prevent duplicate scans
      inputBufferRef.current = "";
      fetchMemberDetails(e.target.value);
    }
  };

  // Status icon and color function
  const getStatusColor = () => {
    switch (scanStatus) {
      case "scanning":
        return "text-primary";
      case "success":
        return "text-success";
      case "error":
        return "text-danger";
      default:
        return "text-muted";
    }
  };

  const getStatusIcon = () => {
    switch (scanStatus) {
      case "scanning":
        return <FaBarcode size={64} className="animate-pulse" />;
      case "success":
        return <FaCheckCircle size={64} className="animate-pulse" />;
      case "error":
        return <FaBarcode size={64} className="animate-pulse" />;
      default:
        return <FaBarcode size={64} className="animate-pulse" />;
    }
  };

  const handleMarkAttendance = (e) => {
    e.preventDefault();
    setLoading(true);
    setMarkingAttendance(true);
    setMarkingAttendanceError("");
    setMarkingAttendanceSuccess("");
    setIsAttendanceMarked(false);
    ApiEvent()
      .addeventparticipant({
        eventId: eventId,
        serialNumber: memberData.serialNumber.toString(),
        onTime: true,
        memberId: null,
      })
      .then((res) => {
        if (res.data) {
          setLoading(false);
          setMemberData("");
          setMarkingAttendanceError("");
          setScannedCode("");
          setScanStatus("ready");
          setIsAttendanceMarked(true);
          setMarkingAttendance(false);
          setMarkingAttendanceSuccess(
            GetSelectedLanguage() === "ur"
              ? res.data + " کی حاضری لگا دی گئی ہے"
              : "Attendance marked for " + res.data
          );
        }
      })
      .catch((err) => {
        setMemberData("");
        setLoading(false);
        setMarkingAttendance(false);
        setScannedCode("");
        let serverMessage = "event_attendance_barcode_error_marking_attendance";
        let errorType = "event_attendance_barcode_error_marking_attendance";

        if (err.response) {
          if (err.response.data?.message) {
            serverMessage = err.response.data.message;
          } else if (typeof err.response.data === "string") {
            serverMessage = err.response.data;
          }

          if (err.response.data?.type) {
            errorType = err.response.data.type;
          } else if (typeof err.response.data === "string") {
            errorType = err.response.data;
          }

          if (err.response.status === 500) {
            setScanStatus("error");
            setMarkingAttendanceError(
              "Serial number might be incorrect or record doesn't exist."
            );
            return;
          }
        }

        if (
          serverMessage.includes("already_marked_attendance") ||
          errorType.includes("already_marked_attendance")
        ) {
          setScanStatus("");
        } else {
          setScanStatus("error");
        }

        setMarkingAttendanceError("Error: " + t(serverMessage));

        if (errorType.includes("invalid_event_participant")) {
          setScanStatus("");
          setMarkingAttendanceError(
            GetSelectedLanguage() === "ur"
              ? +"یہ رفیق " +
                  serverMessage +
                  " اس اجتماع میں شامل ہونے کی اجازت نہیں رکھتا"
              : "This rafiq " + serverMessage + " is not allowed in this Ijtima"
          );
        }
      });
  };

  const deleteEventParticipant = (memberId) => {
    setLoading(true);
    setUnMarkingAttendance(true);
    setMarkingAttendanceError("");
    ApiEvent()
      .deleteEventParticipant(memberId, eventId)
      .then((res) => {
        setLoading(false);
        setMemberData("");
        setMarkingAttendanceError("");
        setScannedCode("");
        setScanStatus("ready");
        setIsAttendanceMarked(true);
        setUnMarkingAttendance(false);
        setMarkingAttendanceSuccess(
          GetSelectedLanguage() === "ur"
            ? "حاضری منسوخ کر دی گئی ہے۔"
            : "Attendance has been unmarked."
        );
      })
      .catch((err) => {
        setMarkingAttendanceError(err.response.data);
        setLoading(false);
        setScanStatus("");
        setMemberData("");
        setUnMarkingAttendance(false);
        setScannedCode("");
      });
  };

  return (
    <div
      className={
        applyMarginTop
          ? "wrapper-custom wrapper wrapper-content animated fadeInRight"
          : ""
      }
    >
      <div className="panel panel-success">
        <div className="panel-heading">
          <Row>
            <Col md="9">
              <h4 className="m-0">
                <GetLanguageString props={"common_attendance"} />
              </h4>
            </Col>{" "}
            {eventIdFromToken ? null : (
              <Col md="3">
                <ButtonGroup className={GetCustomStyles().btn_style}>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => history.goBack()}
                  >
                    {<GetLanguageString props="courses_participants_back" />}
                  </Button>
                </ButtonGroup>
              </Col>
            )}
          </Row>
        </div>
        <div className="panel-body">
          <Row>
            {/* Left Column - Scanning Interface */}
            <Col
              md="6"
              className={
                GetSelectedLanguage() === "ur"
                  ? "border-start ps-md-4"
                  : "border-end pe-md-4"
              }
            >
              {!loading ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "300px", // adjust as needed
                    textAlign: "center",
                  }}
                >
                  <div className={`display-4 mb-3 ${getStatusColor()}`}>
                    {getStatusIcon()}
                  </div>

                  <h2 className="mb-3" style={{ fontWeight: "600" }}>
                    {scanStatus === "scanning"
                      ? t("event_attendance_barcode_scanning")
                      : scanStatus === "success"
                      ? t("event_attendance_barcode_member_verified")
                      : scanStatus === "error"
                      ? t("event_attendance_barcode_scan_failed")
                      : t("event_attendance_barcode_scan")}
                  </h2>
                  <p className="text-muted">
                    {scanStatus === "ready"
                      ? t("event_attendance_barcode_scan_point_scanner")
                      : scanStatus === "scanning"
                      ? t("event_attendance_barcode_reading")
                      : scanStatus === "success"
                      ? t("event_attendance_barcode_confirm_attendance")
                      : t("please_try_again")}
                  </p>

                  {scanStatus === "success" ? null : (
                    <>
                      <p
                        className={
                          GetSelectedLanguage() === "ur"
                            ? "text-muted ur"
                            : "text-muted"
                        }
                      >
                        {GetSelectedLanguage() === "ur" ? "یا" : "or"}
                      </p>
                      <div className="mt-3 w-100">
                        <Row className="justify-content-center">
                          <Col xs="12" sm="8" md="6" lg="4">
                            <Input
                              type="text"
                              autoFocus
                              value={scannedCode}
                              onChange={handleManualInput}
                              onKeyDown={handleManualSubmit}
                              style={{ width: "100%", textAlign: "center" }}
                              placeholder={
                                GetSelectedLanguage() === "en"
                                  ? "Enter Serial Number"
                                  : "سیریل نمبر لکھیں "
                              }
                            />
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "300px", // adjust as needed
                    textAlign: "center",
                  }}
                >
                  <h2 className="mb-3" style={{ fontWeight: "600" }}>
                    <GetLanguageString
                      props={
                        markingAttendance
                          ? "event_attendance_barcode_marking_attendance"
                          : unMarkingAttendance
                          ? "event_attendance_barcode_unmarking_attendance"
                          : "event_attendance_barcode_fetching"
                      }
                    />
                  </h2>
                </div>
              )}
            </Col>
            <Col md="6" className="ps-md-4">
              {!loading ? (
                <>
                  {memberData ? (
                    <Card
                      className="shadow-lg border-0 h-100"
                      style={{ borderRadius: "15px" }}
                    >
                      <div className="card-header bg-success text-white py-3">
                        <div className="d-flex align-items-center">
                          <FaIdCard className="me-2" size={25} />
                          <CardTitle tag="h4" className="m-0">
                            <GetLanguageString props="member_member_detail" />
                          </CardTitle>
                        </div>
                      </div>
                      <CardBody className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          {memberData.memberPhoto ? (
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-light rounded-circle overflow-hidden d-flex justify-content-center align-items-center me-3"
                                style={{ width: "125px", height: "125px" }}
                              >
                                <img
                                  src={memberData.memberPhoto}
                                  alt="Uploaded"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="bg-light rounded-circle p-3 me-3">
                              <FaUser
                                style={{ width: "100px", height: "100px" }}
                                className="text-primary"
                              />
                            </div>
                          )}

                          <div>
                            <h1
                              className="m-0 ur"
                              style={{ fontWeight: "600" }}
                            >
                              {memberData.fullName}
                            </h1>
                          </div>
                        </div>

                        <div className="border-top pt-3">
                          <Row className="mb-2">
                            <Col xs="5" className="text-muted">
                              <GetLanguageString props="member_reg_number" />
                              {" : "}
                            </Col>
                            <Col
                              xs="7"
                              className="font-weight-bold"
                              style={{ fontSize: 15, fontWeight: "bold" }}
                            >
                              {memberData.regNo}
                            </Col>
                          </Row>
                          {memberData?.responsibilityName?.trim() && (
                            <Row className="mb-2">
                              <Col xs="5" className="text-muted">
                                <GetLanguageString props="member_responsibility" />{" "}
                                {" : "}
                              </Col>
                              <Col
                                xs="7"
                                className="font-weight-bold ur"
                                style={{ fontSize: 15, fontWeight: "bold" }}
                              >
                                {memberData.responsibilityName}
                              </Col>
                            </Row>
                          )}

                          <Row className="mb-2">
                            <Col xs="5" className="text-muted">
                              <GetLanguageString props="unit" /> {" : "}
                            </Col>
                            <Col
                              xs="7"
                              className="font-weight-bold ur"
                              style={{ fontSize: 15, fontWeight: "bold" }}
                            >
                              <ol className="breadcrumb">
                                {memberData.unitName
                                  ?.split("،")
                                  ?.map((item1, index) => {
                                    return (
                                      <li
                                        className={
                                          GetSelectedLanguage() === "ur"
                                            ? "breadcrumb-item-ur"
                                            : "breadcrumb-item"
                                        }
                                        key={index}
                                      >
                                        {item1}
                                      </li>
                                    );
                                  })}
                              </ol>
                            </Col>
                          </Row>
                        </div>
                      </CardBody>
                      <div className="card-footer bg-light py-2">
                        <Row className="align-items-center">
                          <Col xs="6">
                            <small className="text-muted">
                              {t("event_attendance_barcode_scanned_at")}{" "}
                              {new Date().toLocaleTimeString()}
                            </small>
                          </Col>
                          <Col
                            xs="6"
                            className={
                              GetSelectedLanguage() === "ur"
                                ? "text-start"
                                : "text-end"
                            }
                          >
                            <Button
                              outline
                              color="danger"
                              className="me-2"
                              onClick={() => {
                                setMemberData("");
                                setScanStatus("ready");
                                setScannedCode("");
                                setMarkingAttendanceError("");
                                setMarkingAttendanceSuccess("");
                                setIsAttendanceMarked(false);
                              }}
                            >
                              <RxCross2
                                size={16}
                                style={{ marginBottom: 2, marginRight: 5 }}
                              />
                              <GetLanguageString props={"common_cancel"} />
                            </Button>
                            {memberData.attendanceExists ? null : (
                              <Button
                                outline
                                disabled={
                                  memberData ? memberData.isExRafiq : false
                                }
                                color="success"
                                onClick={handleMarkAttendance}
                              >
                                <IoCheckmark
                                  size={16}
                                  style={{ marginBottom: 2, marginRight: 5 }}
                                />
                                <GetLanguageString props={"common_confirm"} />
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </div>
                    </Card>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                      <FaBarcode size={64} className="text-muted mb-3" />
                      <h5 className="text-muted">
                        <GetLanguageString
                          props={"event_attendance_barcode_scan_no_scanned"}
                        />
                      </h5>
                      <p className="text-muted text-center">
                        <GetLanguageString
                          props={
                            "event_attendance_barcode_scan_to_show_details"
                          }
                        />
                      </p>
                    </div>
                  )}
                </>
              ) : (
                LoadingSpinner()
              )}
            </Col>
          </Row>
          {error && !markingAttendanceError && (
            <Alert color="danger" style={{ marginTop: "20px" }}>
              <GetLanguageString
                props={"event_attendance_barcode_scan_error"}
                value={scannedCodeForError}
              />
            </Alert>
          )}{" "}
          {markingAttendanceError && (
            <Alert
              color="danger"
              className="d-flex justify-content-between align-items-center p-3"
              style={{ marginTop: "20px" }}
            >
              <span>
                {applyClassToCharacters(markingAttendanceError, "ur")}
              </span>
              {markingAttendanceError.includes(
                "Attendance is already marked for this rafiq" ||
                  markingAttendanceError.includes(
                    " اس رفیق کی حاضری پہلے ہی لگ چکی ہے"
                  )
              ) ? (
                <Button
                  color="danger"
                  size="sm"
                  className="ms-3"
                  onClick={() => deleteEventParticipant(memberData.id)}
                  style={{ fontWeight: 500 }}
                >
                  ✕{" "}
                  <GetLanguageString
                    props={"events_attendance_unmark_attendance"}
                  />
                </Button>
              ) : null}
            </Alert>
          )}
          {isAttendanceMarked && (
            <Alert color="success" style={{ marginTop: "20px" }}>
              {applyClassToCharacters(markingAttendanceSuccess, "ur")}
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(EventBarCodeAttendance);
